import React, {
  type ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Checkbox } from '@atlaskit/checkbox';
import {
  DynamicTableProps,
  DynamicTableStateless,
} from '@atlaskit/dynamic-table';
import {
  type HeadType,
  type RowType,
  type SortOrderType,
} from '@atlaskit/dynamic-table/types';
import Pagination, { type PaginationPropTypes } from '@atlaskit/pagination';
import { Box, Flex, xcss } from '@atlaskit/primitives';

import { type SortData } from 'src/components/pipelines/components/test-management/types';
import {
  addOrToggleSort,
  PAGE_LEN,
} from 'src/components/pipelines/components/test-management/utils';
import { SORT_QUERY_PARAM } from 'src/components/pipelines/constants';
import { useTestManagementQueryParams } from 'src/components/pipelines/hooks/use-test-management-query-params';
import { useAnalytics } from 'src/components/settings/analytics';

import { BulkSelectControlBar } from './BulkSelectControlBar';
import { TestManagementTableEmptyView } from './TestManagementTableEmptyView';

const SELECT_COLUMN_KEY = 'select';

type MultiSelectRenderArgs<RowData> = {
  selectedItems: RowData[];
  selectedRowIndices: number[];
  clearSelection: () => void;
};

type MultiSelectProps<RowData> = {
  multiSelect?: {
    rowData?: RowData[];
    highlightSelectedRows?: boolean;
    selectionLabel: (selectedCount: number) => ReactNode;
    clearSelectionLabel: string;
    renderActions?: (args: MultiSelectRenderArgs<RowData>) => ReactNode;
  };
};

type Props<RowData = RowType> = Pick<DynamicTableProps, 'rows' | 'head'> &
  PaginationPropTypes<any> &
  MultiSelectProps<RowData> & {
    isLoading?: boolean;
    updateMethod?: 'push' | 'replace';
  };

const flexStyle = xcss({
  justifyContent: 'center',
  marginTop: 'space.400',
  marginBottom: 'space.800',
});

const checkboxCellStyle = xcss({
  marginTop: 'space.negative.050',
});

const isIndeterminate = (rowCount: number, selectedCount: number): boolean =>
  selectedCount > 0 && selectedCount < rowCount;

const isDefined = <Value,>(value: Value | undefined): value is Value =>
  value !== undefined;

export const TestManagementTable = <RowData = RowType,>({
  head,
  rows,
  pages,
  isLoading,
  multiSelect,
  updateMethod,
}: Props<RowData>) => {
  const [{ pageIndex, sort }, { setPage, setQueryParam }] =
    useTestManagementQueryParams({ updateMethod });

  const [selectedRowIndices, setSelectedRowIndices] = useState<number[]>([]);

  const { publishButtonClickEvent } = useAnalytics({
    source: 'testManagementTable',
  });

  const onPageChange = (_: SyntheticEvent, selectedIndex: number) => {
    setPage(selectedIndex);
  };

  const onSort = (sortData: SortData) => {
    setQueryParam(SORT_QUERY_PARAM, addOrToggleSort(sortData.key, sort));
    publishButtonClickEvent('sortColumn', { columnKey: sortData.key });
  };

  const { sortKey, sortOrder } = useMemo((): {
    sortKey: string | undefined;
    sortOrder: SortOrderType | undefined;
  } => {
    if (!sort) {
      return { sortKey: undefined, sortOrder: undefined };
    }
    const isDesc = sort.startsWith('-');
    return {
      sortKey: isDesc ? sort.slice(1) : sort,
      sortOrder: isDesc ? 'DESC' : 'ASC',
    };
  }, [sort]);

  const isMultiSelectEnabled = multiSelect !== undefined;
  const highlightSelectedRows = multiSelect?.highlightSelectedRows ?? true;
  const rowCount = rows?.length ?? 0;

  useEffect(() => {
    setSelectedRowIndices([]);
  }, [rows, pageIndex]);

  const clearSelection = useCallback(() => setSelectedRowIndices([]), []);

  const onRowSelect = useCallback((rowIndex: number, isSelected: boolean) => {
    setSelectedRowIndices(prev => {
      if (isSelected) {
        return prev.includes(rowIndex) ? prev : [...prev, rowIndex];
      }
      return prev.filter(index => index !== rowIndex);
    });
  }, []);

  const onSelectAllClick = useCallback(
    (isSelected: boolean) => {
      setSelectedRowIndices(
        isSelected ? Array.from({ length: rowCount }, (_, index) => index) : []
      );
    },
    [rowCount]
  );

  const selectedIndexSet = useMemo(
    () => new Set<number>(selectedRowIndices),
    [selectedRowIndices]
  );

  const selectedItems = useMemo(() => {
    const source = multiSelect?.rowData ?? (rows as RowData[] | undefined);
    if (!source) {
      return [];
    }

    return selectedRowIndices.map(index => source[index]).filter(isDefined);
  }, [multiSelect?.rowData, rows, selectedRowIndices]);

  const selectionActions = multiSelect?.renderActions?.({
    selectedItems,
    selectedRowIndices,
    clearSelection,
  });

  const extendedHead = useMemo<typeof head>(() => {
    if (!isMultiSelectEnabled || !head) {
      return head;
    }

    const selectAllCell = {
      key: SELECT_COLUMN_KEY,
      content: (
        <Checkbox
          isChecked={rowCount > 0 && selectedIndexSet.size === rowCount}
          isIndeterminate={isIndeterminate(rowCount, selectedIndexSet.size)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onSelectAllClick(e.currentTarget.checked)
          }
        />
      ),
      isSortable: false,
      width: 3,
    };

    return {
      ...head,
      cells: [selectAllCell, ...head.cells],
    } satisfies HeadType;
  }, [
    head,
    isMultiSelectEnabled,
    onSelectAllClick,
    rowCount,
    selectedIndexSet,
  ]);

  const extendedRows = useMemo<typeof rows>(() => {
    if (!isMultiSelectEnabled || !rows) {
      return rows;
    }

    return rows.map((row, index) => {
      const isSelected = selectedIndexSet.has(index);

      return {
        ...row,
        cells: [
          {
            key: SELECT_COLUMN_KEY,
            content: (
              <Box xcss={checkboxCellStyle}>
                <Checkbox
                  isChecked={isSelected}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onRowSelect(index, e.currentTarget.checked)
                  }
                />
              </Box>
            ),
          },
          ...row.cells,
        ],
      } satisfies RowType;
    });
  }, [isMultiSelectEnabled, onRowSelect, rows, selectedIndexSet]);

  const highlightedRowIndex = useMemo(() => {
    if (
      !isMultiSelectEnabled ||
      !highlightSelectedRows ||
      selectedIndexSet.size === 0
    ) {
      return undefined;
    }
    return Array.from(selectedIndexSet);
  }, [highlightSelectedRows, isMultiSelectEnabled, selectedIndexSet]);

  return (
    <Box>
      <DynamicTableStateless
        head={extendedHead}
        rows={extendedRows}
        isFixedSize
        isLoading={isLoading}
        rowsPerPage={PAGE_LEN}
        sortKey={sortKey}
        sortOrder={sortOrder}
        totalRows={rowCount}
        onSort={onSort}
        emptyView={<TestManagementTableEmptyView />}
        highlightedRowIndex={highlightedRowIndex}
      />
      {multiSelect && (
        <BulkSelectControlBar
          selectedCount={selectedRowIndices.length}
          selectionLabel={multiSelect.selectionLabel(selectedRowIndices.length)}
          clearSelectionLabel={multiSelect.clearSelectionLabel}
          onClearSelection={clearSelection}
          actions={selectionActions}
        />
      )}
      {!isLoading && (
        <Flex xcss={flexStyle}>
          <Pagination
            pages={pages}
            selectedIndex={pageIndex}
            onChange={onPageChange}
          />
        </Flex>
      )}
    </Box>
  );
};
